/**
 * PublicLeaguePlayoffsPage — /l/:slug/playoffs
 * ─────────────────────────────────────────────────────────────────
 * Public pages overhaul P3 (2026-06-09). The tournaments page:
 * public read of the Phase F playoff brackets for the current
 * season — rounds as columns, scores once games lock, champion
 * highlighted.
 */

import { useEffect, useState } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import {
  container, Section, Card, Chip, EmptyState,
  INK, MUTED, LINE, SURFACE, formatDateNice, formatTimeNice,
} from './pubUi.jsx';

export default function PublicLeaguePlayoffsPage() {
  const { slug } = useParams();
  const { league, currentSeason, accent } = useOutletContext();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (league?.name) document.title = `Playoffs — ${league.name}`;
  }, [league?.name]);

  useEffect(() => {
    if (!currentSeason?.id) { setData({ brackets: [] }); return; }
    let cancelled = false;
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons/${currentSeason.id}/brackets`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ brackets: [] }); });
    return () => { cancelled = true; };
  }, [slug, currentSeason?.id]);

  return (
    <div style={{ ...container, paddingTop: 36 }}>
      <Section eyebrow={currentSeason?.name || 'Tournaments'} title="Playoffs">
        {data === null && <div style={{ color: MUTED }}>Loading…</div>}
        {data?.brackets?.length === 0 && (
          <EmptyState icon="🏆">
            No playoff brackets yet — they appear here once the league sets up the postseason.
          </EmptyState>
        )}
        {data?.brackets?.map(b => <BracketCard key={b.id} bracket={b} slug={slug} accent={accent} />)}
      </Section>
    </div>
  );
}

function BracketCard({ bracket, slug, accent }) {
  const rounds = [...new Set(bracket.slots.map(s => s.round))].sort((a, b) => a - b);
  const maxRound = rounds[rounds.length - 1];
  const label = (r) => {
    const left = maxRound - r;
    if (left === 0) return 'Final';
    if (left === 1) return 'Semifinals';
    if (left === 2) return 'Quarterfinals';
    return `Round ${r}`;
  };
  const finalSlot = bracket.slots.find(s => s.round === maxRound);
  const champion = bracket.status === 'completed' && finalSlot?.winner_team_id
    ? (finalSlot.winner_team_id === finalSlot.home_team_id
        ? finalSlot.home_team_name : finalSlot.away_team_name)
    : null;

  return (
    <Card style={{ padding: 22, marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 20 }}>🏆</span>
        <span style={{ fontWeight: 800, fontSize: 18, color: INK }}>{bracket.name}</span>
        {bracket.division_name && <Chip>{bracket.division_name}</Chip>}
        {champion && (
          <Chip color="#9a6700" bg="#fff3d1">Champions: {champion}</Chip>
        )}
      </div>
      <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 6 }}>
        {rounds.map(r => (
          <div key={r} style={{ minWidth: 230 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>
              {label(r)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, height: '100%', justifyContent: 'space-around' }}>
              {bracket.slots.filter(s => s.round === r).map(s => (
                <PublicSlot key={`${s.round}-${s.position}`} slot={s} slug={slug} accent={accent} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PublicSlot({ slot, slug, accent }) {
  const Side = ({ name, seed, teamId, score }) => {
    const won = slot.winner_team_id && slot.winner_team_id === teamId;
    const lost = slot.winner_team_id && teamId && slot.winner_team_id !== teamId;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        fontSize: 13.5, fontWeight: won ? 800 : 600,
        opacity: lost ? 0.55 : 1, color: INK,
      }}>
        {seed && <span style={{ fontSize: 10, color: MUTED, width: 14 }}>{seed}</span>}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name || <em style={{ color: MUTED }}>TBD</em>}
        </span>
        {score != null && <span style={{ fontWeight: 800 }}>{score}</span>}
        {won && <span style={{ color: '#b8860b' }}>👑</span>}
      </div>
    );
  };
  const inner = (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, background: SURFACE, overflow: 'hidden' }}>
      <Side name={slot.home_team_name} seed={slot.home_seed} teamId={slot.home_team_id} score={slot.home_score} />
      <div style={{ borderTop: `1px solid ${LINE}` }} />
      <Side name={slot.away_team_name} seed={slot.away_seed} teamId={slot.away_team_id} score={slot.away_score} />
      {slot.game_date && (
        <div style={{ padding: '4px 10px 7px', fontSize: 11, color: MUTED }}>
          {formatDateNice(slot.game_date)}{slot.game_time ? ` · ${formatTimeNice(slot.game_time)}` : ''}
          {slot.venue_name ? ` · ${slot.venue_name}` : ''}
        </div>
      )}
    </div>
  );
  return slot.game_id
    ? <Link to={`/l/${slug}/game/${slot.game_id}`} style={{ textDecoration: 'none' }}>{inner}</Link>
    : inner;
}
